'use client';

import { useEffect, useMemo, useState } from 'react';
import config from 'config';
import { ROUTES } from 'constant';
import { usePathname, useRouter } from 'next/navigation';

import {
  Box,
  Flex,
  Tab,
  TabList,
  Tabs as ChakraTabs,
  useTheme
} from '@chakra-ui/react';

import { TabType } from 'types';

const {
  dashboard: { tabs }
} = config;

const Tabs = () => {
  const {
    colors: {
      palette: {
        components: {
          ui: { tabs: styles }
        }
      }
    }
  } = useTheme();
  const router = useRouter();
  const pathname = usePathname();
  const [value, setValue] = useState<string>(ROUTES.dashboard);

  const tabIndex = useMemo(() => {
    const selectedItem = (tabs ?? []).find(({ link }: TabType) =>
      pathname.includes(link)
    );
    return selectedItem ? selectedItem.id : 0;
  }, [pathname]);

  useEffect(() => {
    setValue(pathname);
  }, [pathname]);

  const getCondition = (link: string) => {
    if (value !== ROUTES.dashboard) {
      return value.includes(link) && link !== ROUTES.dashboard;
    }

    return link === ROUTES.dashboard;
  };

  return (
    <ChakraTabs
      position='relative'
      variant='unstyled'
      index={tabIndex}
      bg={styles.tabBackground}
      _after={{
        content: '""',
        position: 'absolute',
        display: 'block',
        height: '100%',
        width: '100vw',
        bg: styles.tabBackground,
        top: 0,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: -1
      }}
      _before={{
        content: '""',
        position: 'absolute',
        display: 'block',
        height: '1px',
        width: '100vw',
        bg: styles.bottomColor,
        bottom: 0,
        left: '50%',
        transform: 'translateX(-50%)',
        opacity: 0.5,
        zIndex: 1
      }}
    >
      <Flex justifyContent='center'>
        <TabList flexGrow='1' justifyContent='space-between' py='20px'>
          {tabs.map(({ link, label }: TabType) => (
            <Tab
              value={value}
              key={label}
              onClick={() => router.push(link)}
              sx={{
                position: 'relative',
                textTransform: 'uppercase',
                fontSize: '13px',
                lineHeight: '18px',
                fontWeight: getCondition(link) ? 700 : 500,
                padding: 0,
                whiteSpace: 'nowrap',
                _after: {
                  content: '""',
                  position: 'absolute',
                  display: 'block',
                  h: '2px',
                  w: '100%',
                  bottom: '-5px',
                  bg: getCondition(link)
                    ? styles.tabSelectedAfterBg
                    : 'transparent',
                  borderRadius: '5px'
                },
                color: styles.tabColor
              }}
            >
              <Box
                px={{ base: 0, md: '10px', xl: '20px', '2xl': '30px' }}
                _after={{
                  display: 'block',
                  content: `"${label}"`,
                  fontWeight: 'bold',
                  opacity: 0,
                  height: 0,
                  textAlign: 'center',
                  overflow: 'hidden',
                  visibility: 'hidden'
                }}
              >
                {label}
              </Box>
            </Tab>
          ))}
        </TabList>
      </Flex>
    </ChakraTabs>
  );
};

export default Tabs;
